import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Star, Clock, TrendingUp, BookOpen, RefreshCw, Mic, Headphones, ChevronRight } from "lucide-react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { GamifiedButton } from "@/components/GamifiedButton";
import { StatCard } from "@/components/StatCard";
import { ProgressRing } from "@/components/ProgressRing";
import { Loading } from "@/components/Feedback";
import { useSeo } from "@/hooks/useSeo";

const PLAN = [
  { icon: Headphones, title: "Échauffement", desc: "10 min d'écoute + répétition", tint: "bg-primary/10 text-primary", to: "/apprendre" },
  { icon: BookOpen, title: "Nouveau verset", desc: "Apprendre 1 nouveau verset", tint: "bg-secondary/10 text-secondary", to: "/apprendre" },
  { icon: RefreshCw, title: "Révision", desc: "Réviser les versets dus aujourd'hui", tint: "bg-emerald-100 text-emerald-600", to: "/reviser" },
  { icon: Mic, title: "Récitation", desc: "Enregistre et fais-toi évaluer", tint: "bg-accent/15 text-yellow-600", to: "/reciter" },
];

export default function Home() {
  useSeo("Accueil", "Ton tableau de bord Quran Coach : objectif du jour, plan d'apprentissage, révisions dues, série et progression de mémorisation du Coran.");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: summary, isLoading } = useQuery({
    queryKey: ["progress-summary"],
    queryFn: async () => (await api.get("/progress/summary")).data,
  });
  const { data: due } = useQuery({
    queryKey: ["reviews-due"],
    queryFn: async () => (await api.get("/reviews/due")).data,
  });

  const startSession = useMutation({
    mutationFn: async () => (await api.post("/sessions", { type: "seance", duration_minutes: 10 })).data,
    onSuccess: async (d) => {
      toast.success(`Séance validée ! +${d.points_earned} points · série ${d.streak} 🔥`);
      await refresh();
      qc.invalidateQueries();
      navigate("/apprendre");
    },
    onError: () => toast.error("Impossible d'enregistrer la séance."),
  });

  if (isLoading) return <Loading full />;

  const totalMastery = summary?.verses_learned
    ? Math.round((summary.verses_mastered / Math.max(1, summary.verses_learned)) * 100)
    : 0;

  return (
    <div className="space-y-8">
      {/* Greeting card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-primary to-secondary rounded-3xl p-6 sm:p-8 text-white">
        <div className="absolute inset-0 islamic-star-pattern opacity-30" />
        <div className="relative flex items-center justify-between gap-4">
          <div>
            <p className="text-white/80 font-semibold">As-salamu alaykum,</p>
            <h1 className="text-3xl sm:text-4xl font-black mb-3">Bonjour {user?.first_name} 👋</h1>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full font-bold text-sm">
              <Clock className="w-4 h-4" /> Objectif du jour : 10 minutes
            </div>
          </div>
          <div className="hidden sm:block">
            <ProgressRing value={totalMastery} size={110} color="#ffffff" label={`${totalMastery}%`} sub="Maîtrise" />
          </div>
        </div>
      </motion.div>

      {/* Plan du jour */}
      <section>
        <h2 className="text-xl font-black text-slate-800 mb-4">Plan du jour</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {PLAN.map((p, i) => (
            <motion.button
              key={p.title}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              onClick={() => navigate(p.to)}
              data-testid={`plan-${p.title.toLowerCase()}`}
              className="group flex items-center gap-4 bg-white p-5 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] text-left hover:-translate-y-1 transition-transform"
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${p.tint}`}>
                <p.icon className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-slate-800 flex items-center gap-2">
                  {p.title}
                  {p.title === "Révision" && due?.length > 0 && (
                    <span className="text-xs bg-red-100 text-red-500 px-2 py-0.5 rounded-full">{due.length}</span>
                  )}
                </div>
                <div className="text-sm text-slate-500">{p.desc}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-primary transition-colors" />
            </motion.button>
          ))}
        </div>
      </section>

      {/* Progression stats */}
      <section>
        <h2 className="text-xl font-black text-slate-800 mb-4">Ma progression</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={TrendingUp} label="Progression" value={`${totalMastery}%`} tint="primary" />
          <StatCard icon={Star} label="Étoiles" value={summary?.stars ?? 0} tint="accent" />
          <StatCard icon={Flame} label="Série" value={`${summary?.streak ?? 0} j`} tint="secondary" />
          <StatCard icon={Clock} label="Temps cumulé" value={`${summary?.total_minutes ?? 0} min`} tint="success" />
        </div>
      </section>

      {/* CTA */}
      <div className="sticky bottom-24 lg:bottom-6 flex justify-center">
        <GamifiedButton
          onClick={() => startSession.mutate()}
          disabled={startSession.isPending}
          className="w-full sm:w-auto px-10 py-4 text-lg"
          data-testid="start-session-btn"
        >
          {startSession.isPending ? "…" : "Commencer ma séance"}
        </GamifiedButton>
      </div>
    </div>
  );
}
